import classNames from 'classnames';
import type { ComponentType, JSX, PropsWithChildren } from 'react';
import React from 'react';
import { components } from 'react-select-modern';

import type { ISelectOptionProps } from './SelectOptions.types';

type TOptionShim<T> = Omit<ISelectOptionProps<T>, 'children' | 'className' | 'getClassNames'> & {
  getClassNames: (name: never, props: never) => string;
};

/* Asserted once here rather than on the props object, so the forwarded props stay checked. */
const Option = components.Option as unknown as ComponentType<
  PropsWithChildren<TOptionShim<unknown>>
>;

/**
 * v5's `Option`, mountable in either host, so one renderer serves both.
 *
 * v3 never passes `getClassNames`, which v5 calls unconditionally — hence the stub. And
 * v5 composes `cx(state, getClassNames(...), className)` where v3's `cx` reads two
 * arguments, so a renderer's `className` must ride in through the stub or vanish.
 */
function SelectOption<T>({
  children,
  className,
  getClassNames,
  ...props
}: Readonly<ISelectOptionProps<T>>): JSX.Element {
  const optionProps: TOptionShim<T> = {
    ...props,
    getClassNames: (name, styleProps): string =>
      classNames(getClassNames?.(name, styleProps), className),
  };

  return <Option {...optionProps}>{children}</Option>;
}

export default SelectOption;
